// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const FOLLOW_UPS = [
  { id: 1, contact: "Mike Thompson", company: "Canstorr Commodities", action: "Send revised partnership terms", dueDate: "2026-02-09", priority: "Urgent", assignedTo: "Tyson Becker", status: "Pending", notes: "Legal team reviewed. Need to adjust revenue share clause before sending." },
  { id: 2, contact: "Sarah Chen", company: "Western Mining Corp", action: "Follow up on proposal response", dueDate: "2026-02-08", priority: "High", assignedTo: "Lee Mehling", status: "Overdue", notes: "Proposal sent Feb 4. Sarah was supposed to respond by Feb 7." },
  { id: 3, contact: "Lisa Nguyen", company: "Apex Insurance Brokers", action: "Send finalized SLA document", dueDate: "2026-02-10", priority: "Urgent", assignedTo: "Lucas Mason", status: "Pending", notes: "SLA almost done. Taylor reviewing financial terms." },
  { id: 4, contact: "Dave Richards", company: "Prairie Fleet Services", action: "Prepare demo environment for Forge Fleet", dueDate: "2026-02-11", priority: "High", assignedTo: "Jordan Lutz", status: "Pending", notes: "Need to load sample fleet data for 45 vehicles." },
  { id: 5, contact: "Amanda Foster", company: "GreenLeaf Cannabis Co", action: "Send compliance feature breakdown", dueDate: "2026-02-07", priority: "Medium", assignedTo: "Lucas Mason", status: "Overdue", notes: "Amanda requested detailed compliance tracking docs after discovery call." },
  { id: 6, contact: "Jennifer Walsh", company: "Northern Health Authority", action: "Address data migration concerns", dueDate: "2026-02-12", priority: "High", assignedTo: "Des Bachynski", status: "Pending", notes: "Jennifer raised concerns about migrating existing patient data. Jordan to provide migration plan." },
  { id: 7, contact: "Tom Bradley", company: "Pacific Tire Warehouse", action: "Send updated pricing proposal", dueDate: "2026-02-13", priority: "Medium", assignedTo: "Lucas Mason", status: "Pending", notes: "Tom wants a discounted rate for annual commitment." },
  { id: 8, contact: "Priya Sharma", company: "BeautyBliss Salons", action: "Schedule product demo", dueDate: "2026-02-14", priority: "Low", assignedTo: "Lucas Mason", status: "Pending", notes: "Priya interested but not in a rush. Scheduling for Valentine's week." },
  { id: 9, contact: "Robert Kim", company: "AutoMax Dealers Group", action: "Send case study and ROI analysis", dueDate: "2026-02-06", priority: "Medium", assignedTo: "Lucas Mason", status: "Overdue", notes: "Robert asked for dealership case study at webinar." },
  { id: 10, contact: "Karen Phillips", company: "HealthFirst Clinics", action: "Check-in on onboarding progress", dueDate: "2026-02-19", priority: "Medium", assignedTo: "Des Bachynski", status: "Completed", notes: "Onboarding going well. 8 staff trained. Next milestone Feb 19." },
];

const PRIORITIES = ["All", "Urgent", "High", "Medium", "Low"];
const STATUSES = ["All", "Pending", "Overdue", "Completed"];
const TEAM = ["All", "Tyson Becker", "Lee Mehling", "Lucas Mason", "Jordan Lutz", "Des Bachynski"];

export default function FollowUpsPage() {
  const [followUps, setFollowUps] = useState(FOLLOW_UPS);
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [assigneeFilter, setAssigneeFilter] = useState("All");
  const [expandedItem, setExpandedItem] = useState(null);

  const filtered = followUps.filter(f => {
    if (priorityFilter !== "All" && f.priority !== priorityFilter) return false;
    if (statusFilter !== "All" && f.status !== statusFilter) return false;
    if (assigneeFilter !== "All" && f.assignedTo !== assigneeFilter) return false;
    return true;
  });

  const pending = followUps.filter(f => f.status === "Pending").length;
  const overdue = followUps.filter(f => f.status === "Overdue").length;
  const completedThisWeek = followUps.filter(f => f.status === "Completed").length;

  const priorityColor = (p) => {
    const colors = { Urgent: "#ef4444", High: "#f97316", Medium: "#eab308", Low: "#64748b" };
    return colors[p] || "#64748b";
  };

  const statusColor = (s) => {
    const colors = { Pending: "#3b82f6", Overdue: "#ef4444", Completed: "#10b981" };
    return colors[s] || "#64748b";
  };

  const toggleComplete = (id) => {
    setFollowUps(prev => prev.map(f => f.id === id ? { ...f, status: f.status === "Completed" ? "Pending" : "Completed" } : f));
  };

  return (
    <div>
      <div className="page-header">
        <div><h1>Follow-Ups</h1><p>Track and manage client follow-up actions</p></div>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total Pending</div><div className="stat-value" style={{ color: "#3b82f6" }}>{pending}</div></div>
        <div className="stat-card"><div className="stat-label">Overdue</div><div className="stat-value" style={{ color: "#ef4444" }}>{overdue}</div><div style={{ fontSize: 12, color: "#ef4444" }}>Needs attention</div></div>
        <div className="stat-card"><div className="stat-label">Completed This Week</div><div className="stat-value" style={{ color: "#10b981" }}>{completedThisWeek}</div></div>
        <div className="stat-card"><div className="stat-label">Avg Response Time</div><div className="stat-value">2.4<span style={{ fontSize: 14, color: "#64748b" }}> days</span></div></div>
      </div>

      {/* Filters */}
      <div className="card" style={{ display: "flex", gap: 12, alignItems: "center", padding: "12px 20px", flexWrap: "wrap" }}>
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} style={{ padding: "8px 12px", background: "#0f172a", border: "1px solid #334155", borderRadius: 6, color: "#e2e8f0", fontSize: 13 }}>
          {PRIORITIES.map(p => <option key={p} value={p}>{p === "All" ? "All Priorities" : p}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: "8px 12px", background: "#0f172a", border: "1px solid #334155", borderRadius: 6, color: "#e2e8f0", fontSize: 13 }}>
          {STATUSES.map(s => <option key={s} value={s}>{s === "All" ? "All Statuses" : s}</option>)}
        </select>
        <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} style={{ padding: "8px 12px", background: "#0f172a", border: "1px solid #334155", borderRadius: 6, color: "#e2e8f0", fontSize: 13 }}>
          {TEAM.map(t => <option key={t} value={t}>{t === "All" ? "All Assignees" : t}</option>)}
        </select>
        {(priorityFilter !== "All" || statusFilter !== "All" || assigneeFilter !== "All") && (
          <button className="btn btn-outline btn-sm" onClick={() => { setPriorityFilter("All"); setStatusFilter("All"); setAssigneeFilter("All"); }}>Clear Filters</button>
        )}
      </div>

      {/* Overdue Section */}
      {filtered.filter(f => f.status === "Overdue").length > 0 && (
        <div className="card" style={{ marginTop: 16, borderLeft: "3px solid #ef4444" }}>
          <div className="card-header"><h3 style={{ color: "#ef4444" }}>Overdue</h3><span className="card-badge" style={{ background: "#ef4444" }}>{filtered.filter(f => f.status === "Overdue").length}</span></div>
          {filtered.filter(f => f.status === "Overdue").map(item => (
            <div key={item.id} style={{ padding: "12px 0", borderBottom: "1px solid #334155", cursor: "pointer" }} onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input type="checkbox" checked={false} onChange={() => toggleComplete(item.id)} style={{ accentColor: "#10b981" }} onClick={e => e.stopPropagation()} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0" }}>{item.action}</div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>{item.contact} - {item.company}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span className="badge" style={{ background: priorityColor(item.priority) + "22", color: priorityColor(item.priority) }}>{item.priority}</span>
                  <span style={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>Due: {item.dueDate}</span>
                  <span style={{ fontSize: 12, color: "#64748b" }}>{item.assignedTo}</span>
                </div>
              </div>
              {expandedItem === item.id && (
                <div style={{ marginTop: 8, marginLeft: 32, padding: "10px 14px", background: "#0f172a", borderRadius: 8 }}>
                  <div style={{ fontSize: 13, color: "#e2e8f0" }}>{item.notes}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pending Section */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>Pending Follow-Ups</h3><span className="card-badge" style={{ background: "#3b82f6" }}>{filtered.filter(f => f.status === "Pending").length}</span></div>
        {filtered.filter(f => f.status === "Pending").map(item => (
          <div key={item.id} style={{ padding: "12px 0", borderBottom: "1px solid #334155", cursor: "pointer" }} onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" checked={false} onChange={() => toggleComplete(item.id)} style={{ accentColor: "#10b981" }} onClick={e => e.stopPropagation()} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0" }}>{item.action}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>{item.contact} - {item.company}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span className="badge" style={{ background: priorityColor(item.priority) + "22", color: priorityColor(item.priority) }}>{item.priority}</span>
                <span style={{ fontSize: 12, color: "#94a3b8" }}>Due: {item.dueDate}</span>
                <span style={{ fontSize: 12, color: "#64748b" }}>{item.assignedTo}</span>
              </div>
            </div>
            {expandedItem === item.id && (
              <div style={{ marginTop: 8, marginLeft: 32, padding: "10px 14px", background: "#0f172a", borderRadius: 8 }}>
                <div style={{ fontSize: 13, color: "#e2e8f0" }}>{item.notes}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Completed Section */}
      {filtered.filter(f => f.status === "Completed").length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header"><h3 style={{ color: "#10b981" }}>Completed</h3><span className="card-badge" style={{ background: "#10b981" }}>{filtered.filter(f => f.status === "Completed").length}</span></div>
          {filtered.filter(f => f.status === "Completed").map(item => (
            <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #334155" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" checked={true} onChange={() => toggleComplete(item.id)} style={{ accentColor: "#10b981" }} />
                <div>
                  <div style={{ fontSize: 13, color: "#64748b", textDecoration: "line-through" }}>{item.action}</div>
                  <div style={{ fontSize: 11, color: "#475569" }}>{item.contact} - {item.company}</div>
                </div>
              </div>
              <span style={{ fontSize: 12, color: "#64748b" }}>{item.assignedTo}</span>
            </div>
          ))}
        </div>
      )}

      {/* By Assignee Breakdown */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>Follow-Ups by Assignee</h3></div>
        <div className="chart-bar-container">
          {TEAM.slice(1).map(person => {
            const count = followUps.filter(f => f.assignedTo === person && f.status !== "Completed").length;
            const overdueCount = followUps.filter(f => f.assignedTo === person && f.status === "Overdue").length;
            return (
              <div key={person} className="chart-bar-row">
                <span className="chart-bar-label" style={{ width: 120 }}>{person.split(" ")[0]}</span>
                <div className="chart-bar-track">
                  <div className={"chart-bar-fill" + (overdueCount > 0 ? " red" : "")} style={{ width: (count / 5 * 100) + "%" }}>{count} open{overdueCount > 0 ? ` (${overdueCount} overdue)` : ""}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
